// src/utils/mockDataClaims.ts
// Progress Payment Claims module — milestones, claim packages, financial data
// Spec: TrafficProcure Progress Payment Claims Design Brief v0.1

// ── Types ──────────────────────────────────────────────────────────

export type MilestoneStatus =
  | 'NOT_YET'          // prerequisite work not yet complete
  | 'ELIGIBLE'         // all deliverables done — ready to claim
  | 'CLAIM_IN_PROGRESS'// a claim package has been opened
  | 'SUBMITTED'        // claim submitted to client
  | 'PAID'             // payment received and confirmed

export type ClaimStatus =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'PENDING_DECLARATION'
  | 'READY_TO_SUBMIT'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'DISPUTED'
  | 'PAID'
  | 'VOID'

export type ClientType = 'TMR' | 'ENERGEX' | 'COUNCIL' | 'PRIVATE'

export type ComponentStatus = 'PENDING' | 'POPULATED' | 'ACCEPTED' | 'FLAGGED'

// ── Display metadata ───────────────────────────────────────────────

export const MILESTONE_STATUS_META: Record<MilestoneStatus, { label: string; bg: string; text: string; dot: string }> = {
  NOT_YET:           { label: 'Not yet claimable', bg: 'bg-gray-100',   text: 'text-gray-500',  dot: 'bg-gray-300' },
  ELIGIBLE:          { label: 'Eligible to claim', bg: 'bg-green-100',  text: 'text-green-700', dot: 'bg-green-500' },
  CLAIM_IN_PROGRESS: { label: 'Claim in progress', bg: 'bg-blue-100',   text: 'text-blue-700',  dot: 'bg-blue-500' },
  SUBMITTED:         { label: 'Submitted',          bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  PAID:              { label: 'Paid',               bg: 'bg-teal-100',   text: 'text-teal-700',  dot: 'bg-teal-500' },
}

export const CLAIM_STATUS_META: Record<ClaimStatus, { label: string; bg: string; text: string }> = {
  DRAFT:               { label: 'Draft',               bg: 'bg-gray-100',   text: 'text-gray-600' },
  IN_REVIEW:           { label: 'In review',            bg: 'bg-amber-100',  text: 'text-amber-700' },
  PENDING_DECLARATION: { label: 'Pending declaration',  bg: 'bg-orange-100', text: 'text-orange-700' },
  READY_TO_SUBMIT:     { label: 'Ready to submit',      bg: 'bg-blue-100',   text: 'text-blue-700' },
  SUBMITTED:           { label: 'Submitted to client',  bg: 'bg-purple-100', text: 'text-purple-700' },
  APPROVED:            { label: 'Approved by client',   bg: 'bg-sky-100',    text: 'text-sky-700' },
  DISPUTED:            { label: 'Disputed',             bg: 'bg-red-100',    text: 'text-red-700' },
  PAID:                { label: 'Paid',                 bg: 'bg-green-100',  text: 'text-green-700' },
  VOID:                { label: 'Void',                 bg: 'bg-gray-100',   text: 'text-gray-400' },
}

export const COMPONENT_STATUS_META: Record<ComponentStatus, { label: string; icon: string; bg: string; text: string }> = {
  PENDING:   { label: 'Pending',   icon: '○', bg: 'bg-gray-100',   text: 'text-gray-400' },
  POPULATED: { label: 'Populated', icon: '◑', bg: 'bg-amber-100',  text: 'text-amber-700' },
  ACCEPTED:  { label: 'Accepted',  icon: '●', bg: 'bg-green-100',  text: 'text-green-700' },
  FLAGGED:   { label: 'Flagged',   icon: '!', bg: 'bg-red-100',    text: 'text-red-700' },
}

// ── Job reference data (self-contained — no import from mockData) ──

export interface ClaimJob {
  id: string
  jobNumber: string
  projectName: string
  clientName: string
  clientType: ClientType
  contractValue: number
  retentionPct: number         // e.g. 5 = 5%
  gstApplicable: boolean
  pmName: string
  pmEmail: string
  contractRef: string
  status: 'ACTIVE' | 'COMPLETED' | 'CLOSED'
  startDate: string
  claimPeriodDay: number       // day of month progress claims are due
}

export const CLAIM_JOBS: ClaimJob[] = [
  {
    id: 'j1',
    jobNumber: 'TP-2026-001',
    projectName: 'Sandgate Rd / Earnshaw Rd Traffic Signal Upgrade',
    clientName: 'Department of Transport and Main Roads',
    clientType: 'TMR',
    contractValue: 1_240_000,
    retentionPct: 5,
    gstApplicable: true,
    pmName: 'Jane Smith',
    pmEmail: 'jane.smith@mcbce.com.au',
    contractRef: 'TMR-CS-2025-04417',
    status: 'ACTIVE',
    startDate: '2026-01-15',
    claimPeriodDay: 25,
  },
  {
    id: 'j2',
    jobNumber: 'TP-2026-002',
    projectName: 'Bowen Hills Substation HV Connection Works',
    clientName: 'Energex Limited',
    clientType: 'ENERGEX',
    contractValue: 680_000,
    retentionPct: 0,
    gstApplicable: true,
    pmName: 'Jane Smith',
    pmEmail: 'jane.smith@mcbce.com.au',
    contractRef: 'ENX-CW-2026-00218',
    status: 'ACTIVE',
    startDate: '2026-02-01',
    claimPeriodDay: 20,
  },
  {
    id: 'j3',
    jobNumber: 'TP-2026-003',
    projectName: 'Maroochydore Rd Intersection Reconstruction',
    clientName: 'Sunshine Coast Council',
    clientType: 'COUNCIL',
    contractValue: 2_100_000,
    retentionPct: 5,
    gstApplicable: true,
    pmName: 'Robert Chen',
    pmEmail: 'robert.chen@mcbce.com.au',
    contractRef: 'SCC-CIVIL-2026-0041',
    status: 'ACTIVE',
    startDate: '2026-03-01',
    claimPeriodDay: 28,
  },
  {
    id: 'j4',
    jobNumber: 'TP-2026-004',
    projectName: 'Virginia ITS Node Commissioning',
    clientName: 'Department of Transport and Main Roads',
    clientType: 'TMR',
    contractValue: 290_000,
    retentionPct: 5,
    gstApplicable: true,
    pmName: 'Jane Smith',
    pmEmail: 'jane.smith@mcbce.com.au',
    contractRef: 'TMR-ITS-2026-00934',
    status: 'ACTIVE',
    startDate: '2026-04-10',
    claimPeriodDay: 25,
  },
]

// ── Blocking check types ───────────────────────────────────────────

export interface BlockingItem {
  type: 'WI' | 'ITP' | 'PO' | 'CERT' | 'INCIDENT'
  ref: string
  description: string
  resolved: boolean
}

// ── Milestones ─────────────────────────────────────────────────────

export interface Milestone {
  id: string
  jobId: string
  reference: string            // MS-TP001-001
  title: string
  sequence: number
  claimAmount: number          // ex GST
  retentionHeld: number        // calculated from job retentionPct
  cumulativeValue: number      // running total inc this milestone
  status: MilestoneStatus
  submissionDeadline?: string
  clientContactName?: string
  clientContactEmail?: string
  linkedWIs: string[]          // WI references that must be complete
  linkedITP: string[]          // ITP hold point refs that must be cleared
  linkedPOs: string[]          // PO numbers that must be Delivered/Paid
  requiredCerts: string[]      // cert descriptions required by client
  claimId?: string             // if a claim has been created
  paidDate?: string
  paidAmount?: number
  blockingItems: BlockingItem[]
  // Phase 2: real FKs for eligibility computation
  linkedDrawingTaskIds?: string[]   // DrawingTask IDs — materials readiness check
  wiIdMap?: Record<string, string>  // WI reference → WI id (for step completion lookup)
}

export const MOCK_MILESTONES: Milestone[] = [

  // ── TP-2026-001 (5 milestones) ─────────────────────────────────

  {
    id: 'ms-j1-001',
    jobId: 'j1',
    reference: 'MS-TP001-001',
    title: 'Milestone 1 — Civil works complete: all foundations and pits installed',
    sequence: 1,
    claimAmount: 248_000,
    retentionHeld: 12_400,
    cumulativeValue: 248_000,
    status: 'PAID',
    clientContactName: 'Bryce Harrington',
    clientContactEmail: 'b.harrington@tmr.qld.gov.au',
    linkedWIs: ['WI-SOP-CIV-001', 'WI-SOP-CIV-002'],
    linkedITP: ['HP-001', 'HP-002', 'HP-003'],
    linkedPOs: ['PO-TP001-001', 'PO-TP001-002', 'PO-TP001-003'],
    requiredCerts: ['Concrete test report (NATA)', 'Survey as-built certificate'],
    claimId: 'claim-j1-001',
    paidDate: '2026-04-15',
    paidAmount: 235_600,
    blockingItems: [],
    linkedDrawingTaskIds: ['tsk002', 'tsk003', 'tsk007'],
    wiIdMap: { 'WI-TP001-T1-01': 'wi-pkg-001', 'WI-SOP-CIV-001': 'wi-sop-001', 'WI-SOP-CIV-002': 'wi-sop-002' },
  },
  {
    id: 'ms-j1-002',
    jobId: 'j1',
    reference: 'MS-TP001-002',
    title: 'Milestone 2 — Cable and conduit installation complete: circuits C1–C12 pulled and terminated',
    sequence: 2,
    claimAmount: 310_000,
    retentionHeld: 15_500,
    cumulativeValue: 558_000,
    status: 'PAID',
    clientContactName: 'Bryce Harrington',
    clientContactEmail: 'b.harrington@tmr.qld.gov.au',
    linkedWIs: ['WI-SOP-ELEC-001', 'WI-SOP-ELEC-004'],
    linkedITP: ['HP-ELEC-01', 'HP-ELEC-02'],
    linkedPOs: ['PO-TP001-004', 'PO-TP001-005'],
    requiredCerts: ['Cable IR test certificate', 'Conduit pressure test report'],
    claimId: 'claim-j1-002',
    paidDate: '2026-05-20',
    paidAmount: 294_500,
    blockingItems: [],
  },
  {
    id: 'ms-j1-003',
    jobId: 'j1',
    reference: 'MS-TP001-003',
    title: 'Milestone 3 — Signal heads and cabinet installation: all heads mounted, cabinet fitted out',
    sequence: 3,
    claimAmount: 372_000,
    retentionHeld: 18_600,
    cumulativeValue: 930_000,
    status: 'CLAIM_IN_PROGRESS',
    submissionDeadline: '2026-06-25',
    clientContactName: 'Bryce Harrington',
    clientContactEmail: 'b.harrington@tmr.qld.gov.au',
    linkedWIs: ['WI-SOP-ELEC-002', 'WI-SOP-ELEC-003'],
    linkedITP: ['HP-ELEC-01', 'HP-COM-01'],
    linkedPOs: ['PO-TP001-006', 'PO-TP001-007', 'PO-TP001-008'],
    requiredCerts: ['SCATS configuration acceptance form', 'Signal head alignment certificate'],
    claimId: 'claim-j1-003',
    blockingItems: [],
  },
  {
    id: 'ms-j1-004',
    jobId: 'j1',
    reference: 'MS-TP001-004',
    title: 'Milestone 4 — SCATS commissioning and TMR handover',
    sequence: 4,
    claimAmount: 248_000,
    retentionHeld: 12_400,
    cumulativeValue: 1_178_000,
    status: 'NOT_YET',
    submissionDeadline: '2026-07-31',
    clientContactName: 'Bryce Harrington',
    clientContactEmail: 'b.harrington@tmr.qld.gov.au',
    linkedWIs: ['WI-SOP-COM-001'],
    linkedITP: ['HP-COM-01', 'HP-COM-02'],
    linkedPOs: [],
    requiredCerts: ['SCATS functional test certificate (TMR ITS)', 'As-constructed drawings (signed)'],
    blockingItems: [
      { type: 'WI', ref: 'WI-SOP-COM-001', description: 'ITS system functional test — SCATS commissioning: Not yet complete', resolved: false },
      { type: 'ITP', ref: 'HP-COM-02', description: 'TMR inspector witness sign-off: Not yet scheduled', resolved: false },
      { type: 'CERT', ref: 'AS-CONST-001', description: 'As-constructed drawings not yet submitted to surveyor', resolved: false },
    ],
  },
  {
    id: 'ms-j1-005',
    jobId: 'j1',
    reference: 'MS-TP001-005',
    title: 'Milestone 5 — Defects liability period end and retention release',
    sequence: 5,
    claimAmount: 62_000,
    retentionHeld: 0,
    cumulativeValue: 1_240_000,
    status: 'NOT_YET',
    submissionDeadline: '2027-01-15',
    clientContactName: 'Bryce Harrington',
    clientContactEmail: 'b.harrington@tmr.qld.gov.au',
    linkedWIs: [],
    linkedITP: [],
    linkedPOs: [],
    requiredCerts: ['Defects liability inspection sign-off (TMR)'],
    blockingItems: [
      { type: 'WI', ref: 'MS-TP001-004', description: 'Milestone 4 must be paid before DLP claim can be initiated', resolved: false },
    ],
  },

  // ── TP-2026-002 (3 milestones) ─────────────────────────────────

  {
    id: 'ms-j2-001',
    jobId: 'j2',
    reference: 'MS-TP002-001',
    title: 'Milestone 1 — HV cable installation complete: joint and terminate 33kV XLPE cable',
    sequence: 1,
    claimAmount: 340_000,
    retentionHeld: 0,
    cumulativeValue: 340_000,
    status: 'PAID',
    clientContactName: 'David Nguyen',
    clientContactEmail: 'd.nguyen@energex.com.au',
    linkedWIs: ['WI-SOP-HV-001', 'WI-SOP-HV-002'],
    linkedITP: ['HP-HV-01', 'HP-HV-02', 'HP-HV-SW-01'],
    linkedPOs: ['PO-TP002-001', 'PO-TP002-002'],
    requiredCerts: ['HV cable test certificate (VLF)', 'Energex commissioning acceptance form'],
    claimId: 'claim-j2-001',
    paidDate: '2026-05-10',
    paidAmount: 340_000,
    blockingItems: [],
  },
  {
    id: 'ms-j2-002',
    jobId: 'j2',
    reference: 'MS-TP002-002',
    title: 'Milestone 2 — Switchgear installation and testing complete',
    sequence: 2,
    claimAmount: 238_000,
    retentionHeld: 0,
    cumulativeValue: 578_000,
    status: 'ELIGIBLE',
    submissionDeadline: '2026-06-20',
    clientContactName: 'David Nguyen',
    clientContactEmail: 'd.nguyen@energex.com.au',
    linkedWIs: ['WI-SOP-HV-002'],
    linkedITP: ['HP-HV-SW-01'],
    linkedPOs: ['PO-TP002-003', 'PO-TP002-004'],
    requiredCerts: ["Energex hold point release — switchgear commissioning", 'Partial discharge test report'],
    blockingItems: [],
  },
  {
    id: 'ms-j2-003',
    jobId: 'j2',
    reference: 'MS-TP002-003',
    title: 'Milestone 3 — Practical completion and Energex handover',
    sequence: 3,
    claimAmount: 102_000,
    retentionHeld: 0,
    cumulativeValue: 680_000,
    status: 'NOT_YET',
    submissionDeadline: '2026-08-20',
    clientContactName: 'David Nguyen',
    clientContactEmail: 'd.nguyen@energex.com.au',
    linkedWIs: ['WI-SOP-HV-002'],
    linkedITP: ['HP-HV-02'],
    linkedPOs: [],
    requiredCerts: ['Energex handover certificate', 'As-built schematic drawings'],
    blockingItems: [
      { type: 'WI', ref: 'MS-TP002-002', description: 'Milestone 2 must be submitted before MS 3 is claimable', resolved: false },
      { type: 'CERT', ref: 'HV-AS-BUILT', description: 'As-built schematic drawings not yet submitted', resolved: false },
    ],
  },

  // ── TP-2026-003 (4 milestones) ─────────────────────────────────

  {
    id: 'ms-j3-001',
    jobId: 'j3',
    reference: 'MS-TP003-001',
    title: 'Milestone 1 — Site establishment and Stage 1 civil works (kerb and channel)',
    sequence: 1,
    claimAmount: 420_000,
    retentionHeld: 21_000,
    cumulativeValue: 420_000,
    status: 'ELIGIBLE',
    submissionDeadline: '2026-06-28',
    clientContactName: 'Patricia Walsh',
    clientContactEmail: 'p.walsh@sunshinecoast.qld.gov.au',
    linkedWIs: ['WI-SOP-CIV-001', 'WI-SOP-CIV-002'],
    linkedITP: ['HP-001', 'HP-002'],
    linkedPOs: ['PO-TP003-001', 'PO-TP003-002', 'PO-TP003-003'],
    requiredCerts: ['Concrete test report (NATA)', 'Compaction test results (geotechnical)'],
    blockingItems: [],
  },
  {
    id: 'ms-j3-002',
    jobId: 'j3',
    reference: 'MS-TP003-002',
    title: 'Milestone 2 — Stage 2 civil works: pavement layers and line marking',
    sequence: 2,
    claimAmount: 630_000,
    retentionHeld: 31_500,
    cumulativeValue: 1_050_000,
    status: 'NOT_YET',
    submissionDeadline: '2026-08-28',
    clientContactName: 'Patricia Walsh',
    clientContactEmail: 'p.walsh@sunshinecoast.qld.gov.au',
    linkedWIs: ['WI-SOP-TC-001'],
    linkedITP: ['HP-001'],
    linkedPOs: ['PO-TP003-004', 'PO-TP003-005'],
    requiredCerts: ['Pavement density test certificate', 'Line marking as-built'],
    blockingItems: [
      { type: 'WI', ref: 'WI-TP003-T1-01', description: 'Traffic management work package TP-2026-003 Task 1.1: Status DRAFT — not yet deployed', resolved: false },
      { type: 'PO', ref: 'PO-TP003-004', description: 'Asphalt supply PO — not yet Delivered', resolved: false },
    ],
  },
  {
    id: 'ms-j3-003',
    jobId: 'j3',
    reference: 'MS-TP003-003',
    title: 'Milestone 3 — Traffic signal installation: poles, heads and controller',
    sequence: 3,
    claimAmount: 525_000,
    retentionHeld: 26_250,
    cumulativeValue: 1_575_000,
    status: 'NOT_YET',
    submissionDeadline: '2026-10-31',
    clientContactName: 'Patricia Walsh',
    clientContactEmail: 'p.walsh@sunshinecoast.qld.gov.au',
    linkedWIs: ['WI-SOP-ELEC-002', 'WI-SOP-ELEC-003'],
    linkedITP: ['HP-ELEC-01', 'HP-COM-01'],
    linkedPOs: ['PO-TP003-006'],
    requiredCerts: ['SCATS functional test certificate', 'Signal alignment certificate'],
    blockingItems: [
      { type: 'WI', ref: 'MS-TP003-002', description: 'Milestone 2 must be complete before signals installation can begin', resolved: false },
    ],
  },
  {
    id: 'ms-j3-004',
    jobId: 'j3',
    reference: 'MS-TP003-004',
    title: 'Milestone 4 — Practical completion and council handover',
    sequence: 4,
    claimAmount: 525_000,
    retentionHeld: 0,
    cumulativeValue: 2_100_000,
    status: 'NOT_YET',
    submissionDeadline: '2026-12-31',
    clientContactName: 'Patricia Walsh',
    clientContactEmail: 'p.walsh@sunshinecoast.qld.gov.au',
    linkedWIs: [],
    linkedITP: [],
    linkedPOs: [],
    requiredCerts: ['Certificate of practical completion (council sign-off)', 'As-constructed drawings'],
    blockingItems: [
      { type: 'WI', ref: 'MS-TP003-003', description: 'Milestones 2 and 3 must be paid before PC claim is claimable', resolved: false },
    ],
  },

  // ── TP-2026-004 (3 milestones) ─────────────────────────────────

  {
    id: 'ms-j4-001',
    jobId: 'j4',
    reference: 'MS-TP004-001',
    title: 'Milestone 1 — Field installation complete: controller, pits, and fibre splice',
    sequence: 1,
    claimAmount: 145_000,
    retentionHeld: 7_250,
    cumulativeValue: 145_000,
    status: 'NOT_YET',
    submissionDeadline: '2026-07-25',
    clientContactName: 'Bryce Harrington',
    clientContactEmail: 'b.harrington@tmr.qld.gov.au',
    linkedWIs: ['WI-SOP-ELEC-003', 'WI-SOP-COM-002'],
    linkedITP: ['HP-ELEC-01'],
    linkedPOs: ['PO-TP004-001', 'PO-TP004-002'],
    requiredCerts: ['OTDR fibre test certificate', 'Controller factory acceptance test report'],
    blockingItems: [
      { type: 'WI', ref: 'WI-SOP-COM-002', description: 'Fibre optic splice SOP: Status DRAFT — not yet approved', resolved: false },
      { type: 'PO', ref: 'PO-TP004-002', description: 'Fibre cable supply PO — status APPROVED, not yet Delivered', resolved: false },
    ],
  },
  {
    id: 'ms-j4-002',
    jobId: 'j4',
    reference: 'MS-TP004-002',
    title: 'Milestone 2 — SCATS commissioning and TMR ITS acceptance',
    sequence: 2,
    claimAmount: 108_750,
    retentionHeld: 5_437.50,
    cumulativeValue: 253_750,
    status: 'NOT_YET',
    submissionDeadline: '2026-08-25',
    clientContactName: 'Bryce Harrington',
    clientContactEmail: 'b.harrington@tmr.qld.gov.au',
    linkedWIs: ['WI-SOP-COM-001'],
    linkedITP: ['HP-COM-01', 'HP-COM-02'],
    linkedPOs: [],
    requiredCerts: ['SCATS functional test certificate', 'TMR ITS acceptance sign-off'],
    blockingItems: [
      { type: 'WI', ref: 'MS-TP004-001', description: 'Milestone 1 must be complete first', resolved: false },
    ],
  },
  {
    id: 'ms-j4-003',
    jobId: 'j4',
    reference: 'MS-TP004-003',
    title: 'Milestone 3 — 12-month ITS operational performance and retention release',
    sequence: 3,
    claimAmount: 36_250,
    retentionHeld: 0,
    cumulativeValue: 290_000,
    status: 'NOT_YET',
    submissionDeadline: '2027-08-25',
    clientContactName: 'Bryce Harrington',
    clientContactEmail: 'b.harrington@tmr.qld.gov.au',
    linkedWIs: [],
    linkedITP: [],
    linkedPOs: [],
    requiredCerts: ['12-month ITS operational report', 'Retention release form'],
    blockingItems: [
      { type: 'WI', ref: 'MS-TP004-002', description: 'Milestone 2 must be paid before retention release', resolved: false },
    ],
  },
]

// ── Claim component data types ─────────────────────────────────────

export interface FinancialSummary {
  milestoneAmount: number
  retentionHeld: number
  netClaimed: number
  gstAmount: number
  totalIncGST: number
  previousPayments: number
  balanceRemaining: number
  invoiceNumber: string
  poLineItems: { description: string; supplier: string; amount: number; status: string }[]
  labourCostTotal: number
  materialsCostTotal: number
}

export interface EvidencePhoto {
  id: string
  wiRef: string
  stepTitle: string
  timestamp: string
  gpsCoordsLat: number
  gpsCoordsLng: number
  crew: string
  description: string
  included: boolean
}

export interface SupplierPaymentRecord {
  supplierId: string
  supplierName: string
  abn: string
  poRef: string
  amount: number
  invoiceDate: string
  paidDate?: string
  status: 'PAID' | 'UNPAID' | 'NOT_YET_DUE'
}

export interface QualityCertificate {
  id: string
  type: 'ITP_CLEARANCE' | 'AS_BUILT' | 'THIRD_PARTY' | 'SWMS'
  title: string
  ref: string
  issuedBy: string
  issuedDate: string
  result: string
  attached: boolean
}

export interface ProgressClaim {
  id: string
  jobId: string
  milestoneId: string
  claimRef: string              // PC-TP001-003
  status: ClaimStatus
  clientType: ClientType
  // Component statuses
  componentFS: ComponentStatus
  componentEW: ComponentStatus
  componentSD: ComponentStatus
  componentFD: ComponentStatus
  componentQC: ComponentStatus
  // Component data
  financialSummary: FinancialSummary
  evidencePhotos: EvidencePhoto[]
  supplierPayments: SupplierPaymentRecord[]
  qualityCerts: QualityCertificate[]
  // Workflow
  declarationSignedBy?: string
  declarationSignedAt?: string
  submittedAt?: string
  submittedBy?: string
  clientResponseDate?: string
  clientApprovedAmount?: number
  disputeReason?: string
  paidDate?: string
  paidAmount?: number
  voidReason?: string
  // Meta
  createdById: string
  createdByName: string
  createdAt: string
  updatedAt: string
  internalNotes?: string
}

// ── Claim records ──────────────────────────────────────────────────

export const MOCK_CLAIMS: ProgressClaim[] = [

  // Claim for MS-TP001-003 — IN_REVIEW (the active one being built)
  {
    id: 'claim-j1-003',
    jobId: 'j1',
    milestoneId: 'ms-j1-003',
    claimRef: 'PC-TP001-003',
    status: 'IN_REVIEW',
    clientType: 'TMR',
    componentFS: 'ACCEPTED',
    componentEW: 'POPULATED',
    componentSD: 'FLAGGED',
    componentFD: 'POPULATED',
    componentQC: 'PENDING',
    financialSummary: {
      milestoneAmount: 372_000,
      retentionHeld: 18_600,
      netClaimed: 353_400,
      gstAmount: 35_340,
      totalIncGST: 388_740,
      previousPayments: 530_100,   // MS1 + MS2 net of retention
      balanceRemaining: 310_000,   // contract less all claimed
      invoiceNumber: 'INV-MCBCE-2026-047',
      poLineItems: [
        { description: 'LED signal heads × 24 (3-aspect)', supplier: 'Aldridge Traffic Architects', amount: 88_320, status: 'DELIVERED' },
        { description: 'SCATS controller cabinet — R900S', supplier: 'Aldridge Traffic Architects', amount: 41_200, status: 'DELIVERED' },
        { description: 'Signal pole hardware (DIN base plates × 8)', supplier: 'Saferoads Queensland', amount: 24_640, status: 'DELIVERED' },
        { description: 'Electrical installation labour — signal head mounting', supplier: 'Internal labour', amount: 67_200, status: 'PAID' },
        { description: 'Cabinet fit-out and terminations', supplier: 'Internal labour', amount: 48_000, status: 'PAID' },
      ],
      labourCostTotal: 115_200,
      materialsCostTotal: 154_160,
    },
    evidencePhotos: [
      { id: 'ep-001', wiRef: 'WI-SOP-ELEC-002', stepTitle: 'Mount signal head bracket to pole', timestamp: '2026-06-10T08:45:00Z', gpsCoordsLat: -27.4108, gpsCoordsLng: 153.0304, crew: 'Tony Rivera', description: 'NW corner pole — bracket installed at 4.5m, signal head attached and aligned per J1-E02 Sheet 1', included: true },
      { id: 'ep-002', wiRef: 'WI-SOP-ELEC-002', stepTitle: 'Hang and align signal head', timestamp: '2026-06-10T09:15:00Z', gpsCoordsLat: -27.4108, gpsCoordsLng: 153.0304, crew: 'Tony Rivera', description: 'Signal head alignment confirmed — inclinometer reading 0.2° from vertical', included: true },
      { id: 'ep-003', wiRef: 'WI-SOP-ELEC-003', stepTitle: 'Photograph controller before fit-out', timestamp: '2026-06-12T07:30:00Z', gpsCoordsLat: -27.4110, gpsCoordsLng: 153.0307, crew: 'Tony Rivera', description: 'R900S cabinet interior before field wiring — controller serial ENX-R900-24-0419', included: true },
      { id: 'ep-004', wiRef: 'WI-SOP-ELEC-003', stepTitle: 'Load and verify SCATS configuration file', timestamp: '2026-06-12T14:00:00Z', gpsCoordsLat: -27.4110, gpsCoordsLng: 153.0307, crew: 'Tony Rivera', description: 'SCATS software showing loaded config file v2.1 — intersection ID 10247 assigned', included: true },
      { id: 'ep-005', wiRef: 'WI-SOP-ELEC-003', stepTitle: 'Completed fit-out photo record', timestamp: '2026-06-12T16:30:00Z', gpsCoordsLat: -27.4110, gpsCoordsLng: 153.0307, crew: 'Tony Rivera', description: 'Completed cabinet interior — all wiring dressed and labelled. Door sealed.', included: true },
      { id: 'ep-006', wiRef: 'WI-SOP-ELEC-002', stepTitle: 'Mount signal head bracket to pole', timestamp: '2026-06-13T08:00:00Z', gpsCoordsLat: -27.4112, gpsCoordsLng: 153.0301, crew: 'Sam Nguyen', description: 'SE corner pole — all 3 signal heads mounted and terminated. AS-built torque recorded.', included: true },
      { id: 'ep-007', wiRef: 'WI-SOP-ALL-002', stepTitle: 'EWP pre-start inspection', timestamp: '2026-06-13T07:00:00Z', gpsCoordsLat: -27.4112, gpsCoordsLng: 153.0301, crew: 'Sam Nguyen', description: 'EWP SRT-22 pre-start — PASS. No defects noted. Tag current.', included: false },
    ],
    supplierPayments: [
      { supplierId: 'sup001', supplierName: 'Aldridge Traffic Architects Pty Ltd', abn: '42 007 135 844', poRef: 'PO-TP001-006', amount: 129_520, invoiceDate: '2026-06-05', paidDate: '2026-06-15', status: 'PAID' },
      { supplierId: 'sup002', supplierName: 'Saferoads Queensland Pty Ltd', abn: '89 124 603 271', poRef: 'PO-TP001-007', amount: 24_640, invoiceDate: '2026-05-28', paidDate: '2026-06-10', status: 'PAID' },
      { supplierId: 'sup003', supplierName: 'Blackwoods Australia', abn: '59 004 400 405', poRef: 'PO-TP001-008', amount: 8_940, invoiceDate: '2026-06-01', status: 'UNPAID' },
    ],
    qualityCerts: [
      { id: 'qc-001', type: 'ITP_CLEARANCE', title: 'Pre-energisation wiring check — HP-ELEC-01', ref: 'ITP-TP001-HP-ELEC-01', issuedBy: 'Jane Smith (PM)', issuedDate: '2026-06-12', result: 'CLEARED', attached: true },
      { id: 'qc-002', type: 'AS_BUILT', title: 'Signal head torque and alignment record', ref: 'WI-SOP-ELEC-002 as-built', issuedBy: 'Tony Rivera', issuedDate: '2026-06-13', result: 'All bolts at specified torque. Alignment within ±0.2°', attached: true },
      { id: 'qc-003', type: 'AS_BUILT', title: 'SCATS configuration acceptance', ref: 'WI-SOP-ELEC-003 as-built', issuedBy: 'Tony Rivera', issuedDate: '2026-06-12', result: 'Config v2.1 loaded. Intersection ID 10247. Communication link active.', attached: true },
      { id: 'qc-004', type: 'THIRD_PARTY', title: 'SCATS configuration acceptance form (TMR ITS)', ref: 'TMR-ITS-ACCEPT-2026-047', issuedBy: 'TMR ITS Branch', issuedDate: '', result: 'Pending — scheduled 24 Jun 2026', attached: false },
    ],
    createdById: 'u4', createdByName: 'Sarah Lee',
    createdAt: '2026-06-18T09:00:00Z', updatedAt: '2026-06-21T14:00:00Z',
    internalNotes: 'SD flagged — Blackwoods invoice outstanding ($8,940). PC to confirm payment date before PM sign-off. TMR acceptance cert expected 24 Jun.',
  },

  // Claim for MS-TP002-002 — READY_TO_SUBMIT
  {
    id: 'claim-j2-002',
    jobId: 'j2',
    milestoneId: 'ms-j2-002',
    claimRef: 'PC-TP002-002',
    status: 'READY_TO_SUBMIT',
    clientType: 'ENERGEX',
    componentFS: 'ACCEPTED',
    componentEW: 'ACCEPTED',
    componentSD: 'ACCEPTED',
    componentFD: 'ACCEPTED',
    componentQC: 'ACCEPTED',
    financialSummary: {
      milestoneAmount: 238_000,
      retentionHeld: 0,
      netClaimed: 238_000,
      gstAmount: 23_800,
      totalIncGST: 261_800,
      previousPayments: 340_000,
      balanceRemaining: 102_000,
      invoiceNumber: 'INV-MCBCE-2026-051',
      poLineItems: [
        { description: 'SF6 switchgear unit — 33kV ring main', supplier: 'Schneider Electric', amount: 142_000, status: 'DELIVERED' },
        { description: 'HV switchgear installation labour', supplier: 'Internal labour', amount: 52_400, status: 'PAID' },
        { description: 'Partial discharge testing — NATA lab', supplier: 'PowerTest Queensland', amount: 12_600, status: 'PAID' },
      ],
      labourCostTotal: 52_400,
      materialsCostTotal: 154_600,
    },
    evidencePhotos: [
      { id: 'ep-j2-001', wiRef: 'WI-SOP-HV-002', stepTitle: 'Prove dead — test before touch', timestamp: '2026-06-02T09:00:00Z', gpsCoordsLat: -27.4450, gpsCoordsLng: 153.0210, crew: 'Tony Rivera', description: 'HV voltage indicator confirming cable dead at all approach points before switchgear installation', included: true },
      { id: 'ep-j2-002', wiRef: 'WI-SOP-HV-002', stepTitle: 'Apply earths — record positions', timestamp: '2026-06-02T09:30:00Z', gpsCoordsLat: -27.4450, gpsCoordsLng: 153.0210, crew: 'Tony Rivera', description: 'Earth clamps applied at all three earthing positions per HVSP', included: true },
      { id: 'ep-j2-003', wiRef: 'WI-SOP-HV-001', stepTitle: 'HOLD POINT — joint inspection before reinstatement', timestamp: '2026-06-04T14:00:00Z', gpsCoordsLat: -27.4450, gpsCoordsLng: 153.0210, crew: 'Jane Smith', description: 'PM inspection of completed switchgear installation — all terminations confirmed correct', included: true },
    ],
    supplierPayments: [
      { supplierId: 'sup010', supplierName: 'Schneider Electric (Aust) Pty Ltd', abn: '21 004 851 162', poRef: 'PO-TP002-003', amount: 142_000, invoiceDate: '2026-05-20', paidDate: '2026-06-05', status: 'PAID' },
      { supplierId: 'sup011', supplierName: 'PowerTest Queensland Pty Ltd', abn: '77 133 245 891', poRef: 'PO-TP002-004', amount: 12_600, invoiceDate: '2026-06-08', paidDate: '2026-06-12', status: 'PAID' },
    ],
    qualityCerts: [
      { id: 'qc-j2-001', type: 'ITP_CLEARANCE', title: 'Energex HP release — switchgear commissioning', ref: 'ITP-TP002-HP-HV-SW-01', issuedBy: 'David Nguyen (Energex)', issuedDate: '2026-06-10', result: 'CLEARED', attached: true },
      { id: 'qc-j2-002', type: 'THIRD_PARTY', title: 'Partial discharge test report (NATA)', ref: 'PD-REPORT-2026-0412', issuedBy: 'PowerTest Queensland', issuedDate: '2026-06-09', result: 'PASS — PD level <5pC at 1.7U0', attached: true },
    ],
    declarationSignedBy: 'Jane Smith',
    declarationSignedAt: '2026-06-20T11:00:00Z',
    createdById: 'u2', createdByName: 'Jane Smith',
    createdAt: '2026-06-14T10:00:00Z', updatedAt: '2026-06-20T11:00:00Z',
  },

  // Claim for MS-TP003-001 — DRAFT (just initiated)
  {
    id: 'claim-j3-001',
    jobId: 'j3',
    milestoneId: 'ms-j3-001',
    claimRef: 'PC-TP003-001',
    status: 'DRAFT',
    clientType: 'COUNCIL',
    componentFS: 'POPULATED',
    componentEW: 'PENDING',
    componentSD: 'PENDING',
    componentFD: 'PENDING',
    componentQC: 'PENDING',
    financialSummary: {
      milestoneAmount: 420_000,
      retentionHeld: 21_000,
      netClaimed: 399_000,
      gstAmount: 39_900,
      totalIncGST: 438_900,
      previousPayments: 0,
      balanceRemaining: 1_680_000,
      invoiceNumber: 'INV-MCBCE-2026-055',
      poLineItems: [
        { description: 'Concrete supply — footings and kerb (Grade 40)', supplier: 'Boral Construction Materials', amount: 68_400, status: 'DELIVERED' },
        { description: 'Formwork and reinforcing steel', supplier: 'Steel Force Queensland', amount: 44_200, status: 'DELIVERED' },
        { description: 'Excavation and earthworks — civil subcontract', supplier: 'Dirt & Rock Pty Ltd', amount: 112_000, status: 'DELIVERED' },
        { description: 'Civil installation labour', supplier: 'Internal labour', amount: 98_000, status: 'PAID' },
      ],
      labourCostTotal: 98_000,
      materialsCostTotal: 224_600,
    },
    evidencePhotos: [],
    supplierPayments: [
      { supplierId: 'sup020', supplierName: 'Boral Construction Materials Group Ltd', abn: '12 004 796 249', poRef: 'PO-TP003-001', amount: 68_400, invoiceDate: '2026-06-01', paidDate: '2026-06-14', status: 'PAID' },
      { supplierId: 'sup021', supplierName: 'Steel Force Queensland Pty Ltd', abn: '34 091 228 175', poRef: 'PO-TP003-002', amount: 44_200, invoiceDate: '2026-06-05', status: 'UNPAID' },
      { supplierId: 'sup022', supplierName: 'Dirt & Rock Pty Ltd', abn: '67 158 902 431', poRef: 'PO-TP003-003', amount: 112_000, invoiceDate: '2026-06-10', status: 'UNPAID' },
    ],
    qualityCerts: [
      { id: 'qc-j3-001', type: 'ITP_CLEARANCE', title: 'Excavation depth inspection — HP-001', ref: 'ITP-TP003-HP-001', issuedBy: 'Mike Walsh (PC)', issuedDate: '2026-06-08', result: 'CLEARED', attached: true },
      { id: 'qc-j3-002', type: 'THIRD_PARTY', title: 'Concrete test report (NATA) — footings pour 1', ref: 'NATA-2026-CT-0889', issuedBy: 'NATA Cert Lab Queensland', issuedDate: '2026-06-12', result: '28-day break 44.2 MPa — PASS (spec 40 MPa min)', attached: true },
      { id: 'qc-j3-003', type: 'THIRD_PARTY', title: 'Compaction test results (geotechnical)', ref: 'GEO-2026-CPT-0341', issuedBy: 'GeoCert Testing', issuedDate: '', result: 'Pending — test scheduled 24 Jun 2026', attached: false },
    ],
    createdById: 'u6', createdByName: 'Mike Walsh',
    createdAt: '2026-06-21T09:00:00Z', updatedAt: '2026-06-21T09:00:00Z',
    internalNotes: 'Auto-populated from platform data. Evidence of works and SD still need PM/PC review. Awaiting geotechnical test cert.',
  },

  // Historical paid claims (for financial history context)
  {
    id: 'claim-j1-001',
    jobId: 'j1',
    milestoneId: 'ms-j1-001',
    claimRef: 'PC-TP001-001',
    status: 'PAID',
    clientType: 'TMR',
    componentFS: 'ACCEPTED', componentEW: 'ACCEPTED', componentSD: 'ACCEPTED', componentFD: 'ACCEPTED', componentQC: 'ACCEPTED',
    financialSummary: {
      milestoneAmount: 248_000, retentionHeld: 12_400, netClaimed: 235_600,
      gstAmount: 23_560, totalIncGST: 259_160, previousPayments: 0, balanceRemaining: 992_000,
      invoiceNumber: 'INV-MCBCE-2026-028',
      poLineItems: [],
      labourCostTotal: 78_000, materialsCostTotal: 145_000,
    },
    evidencePhotos: [],
    supplierPayments: [],
    qualityCerts: [],
    declarationSignedBy: 'Jane Smith', declarationSignedAt: '2026-03-28T10:00:00Z',
    submittedAt: '2026-03-30T14:00:00Z', submittedBy: 'Jane Smith',
    clientResponseDate: '2026-04-10T00:00:00Z',
    clientApprovedAmount: 235_600,
    paidDate: '2026-04-15', paidAmount: 235_600,
    createdById: 'u2', createdByName: 'Jane Smith',
    createdAt: '2026-03-25T09:00:00Z', updatedAt: '2026-04-15T09:00:00Z',
  },
  {
    id: 'claim-j1-002',
    jobId: 'j1',
    milestoneId: 'ms-j1-002',
    claimRef: 'PC-TP001-002',
    status: 'PAID',
    clientType: 'TMR',
    componentFS: 'ACCEPTED', componentEW: 'ACCEPTED', componentSD: 'ACCEPTED', componentFD: 'ACCEPTED', componentQC: 'ACCEPTED',
    financialSummary: {
      milestoneAmount: 310_000, retentionHeld: 15_500, netClaimed: 294_500,
      gstAmount: 29_450, totalIncGST: 323_950, previousPayments: 235_600, balanceRemaining: 682_000,
      invoiceNumber: 'INV-MCBCE-2026-039',
      poLineItems: [],
      labourCostTotal: 92_000, materialsCostTotal: 172_000,
    },
    evidencePhotos: [],
    supplierPayments: [],
    qualityCerts: [],
    declarationSignedBy: 'Jane Smith', declarationSignedAt: '2026-05-12T10:00:00Z',
    submittedAt: '2026-05-14T09:00:00Z', submittedBy: 'Jane Smith',
    clientResponseDate: '2026-05-18T00:00:00Z',
    clientApprovedAmount: 294_500,
    paidDate: '2026-05-20', paidAmount: 294_500,
    createdById: 'u2', createdByName: 'Jane Smith',
    createdAt: '2026-05-08T09:00:00Z', updatedAt: '2026-05-20T09:00:00Z',
  },
  {
    id: 'claim-j2-001',
    jobId: 'j2',
    milestoneId: 'ms-j2-001',
    claimRef: 'PC-TP002-001',
    status: 'PAID',
    clientType: 'ENERGEX',
    componentFS: 'ACCEPTED', componentEW: 'ACCEPTED', componentSD: 'ACCEPTED', componentFD: 'ACCEPTED', componentQC: 'ACCEPTED',
    financialSummary: {
      milestoneAmount: 340_000, retentionHeld: 0, netClaimed: 340_000,
      gstAmount: 34_000, totalIncGST: 374_000, previousPayments: 0, balanceRemaining: 340_000,
      invoiceNumber: 'INV-MCBCE-2026-033',
      poLineItems: [],
      labourCostTotal: 128_000, materialsCostTotal: 168_000,
    },
    evidencePhotos: [], supplierPayments: [], qualityCerts: [],
    declarationSignedBy: 'Jane Smith', declarationSignedAt: '2026-04-28T10:00:00Z',
    submittedAt: '2026-04-30T09:00:00Z', submittedBy: 'Jane Smith',
    clientResponseDate: '2026-05-06T00:00:00Z',
    clientApprovedAmount: 340_000,
    paidDate: '2026-05-10', paidAmount: 340_000,
    createdById: 'u2', createdByName: 'Jane Smith',
    createdAt: '2026-04-25T09:00:00Z', updatedAt: '2026-05-10T09:00:00Z',
  },
]

// ── Helper functions ───────────────────────────────────────────────

export function getMilestonesForJob(jobId: string): Milestone[] {
  return MOCK_MILESTONES.filter(m => m.jobId === jobId).sort((a, b) => a.sequence - b.sequence)
}

export function getClaimsForJob(jobId: string): ProgressClaim[] {
  return MOCK_CLAIMS.filter(c => c.jobId === jobId)
}

export function getActiveClaims(): ProgressClaim[] {
  return MOCK_CLAIMS.filter(c => !['PAID', 'VOID'].includes(c.status))
}

export function calcJobFinancials(jobId: string) {
  const milestones = getMilestonesForJob(jobId)
  const job = CLAIM_JOBS.find(j => j.id === jobId)!
  const paidClaims = MOCK_CLAIMS.filter(c => c.jobId === jobId && c.status === 'PAID')
  const totalPaid = paidClaims.reduce((s, c) => s + (c.paidAmount ?? 0), 0)
  const totalRetentionHeld = milestones
    .filter(m => ['PAID', 'CLAIM_IN_PROGRESS', 'SUBMITTED'].includes(m.status))
    .reduce((s, m) => s + m.retentionHeld, 0)
  const totalClaimed = paidClaims.reduce((s, c) => s + c.financialSummary.netClaimed, 0)
  const activeClaim = MOCK_CLAIMS.find(c => c.jobId === jobId && !['PAID', 'VOID'].includes(c.status))
  return {
    contractValue: job.contractValue,
    totalClaimed,
    totalPaid,
    totalRetentionHeld,
    balance: job.contractValue - totalClaimed,
    pctComplete: Math.round(totalClaimed / job.contractValue * 100),
    activeClaim,
  }
}

export function countComponentsAccepted(claim: ProgressClaim): number {
  return [claim.componentFS, claim.componentEW, claim.componentSD, claim.componentFD, claim.componentQC]
    .filter(s => s === 'ACCEPTED').length
}

export function hasFlaggedComponent(claim: ProgressClaim): boolean {
  return [claim.componentFS, claim.componentEW, claim.componentSD, claim.componentFD, claim.componentQC]
    .some(s => s === 'FLAGGED')
}

// ── Phase 2: localStorage-backed live milestone & claim state ─────
// Milestones and claims are now mutable within a session — status
// can be updated by computeMilestoneEligibility() and claim actions.

const MILESTONES_KEY = 'tp_live_milestones'
const CLAIMS_KEY     = 'tp_live_claims'

function canStore2() { return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined' }
function readLS<T>(key: string, fallback: T): T {
  if (!canStore2()) return fallback
  try { const r = window.localStorage.getItem(key); return r ? JSON.parse(r) as T : fallback } catch { return fallback }
}
function writeLS<T>(key: string, v: T) { if (canStore2()) window.localStorage.setItem(key, JSON.stringify(v)) }

let _ms_seeded = false
function ensureMilestoneSeeded() {
  if (_ms_seeded || !canStore2()) return
  _ms_seeded = true
  // Always reseed from latest mock data to pick up any code changes
  writeLS(MILESTONES_KEY, MOCK_MILESTONES)
  writeLS(CLAIMS_KEY, MOCK_CLAIMS)
}

export function getLiveMilestones(): Milestone[] {
  ensureMilestoneSeeded(); return readLS<Milestone[]>(MILESTONES_KEY, MOCK_MILESTONES)
}
export function getLiveClaims(): ProgressClaim[] {
  ensureMilestoneSeeded(); return readLS<ProgressClaim[]>(CLAIMS_KEY, MOCK_CLAIMS)
}
export function getMilestonesForJob2(jobId: string): Milestone[] {
  return getLiveMilestones().filter(m => m.jobId === jobId)
}
export function updateMilestone(id: string, changes: Partial<Milestone>): Milestone | null {
  const all = getLiveMilestones(); const idx = all.findIndex(m => m.id === id)
  if (idx === -1) return null
  all[idx] = { ...all[idx], ...changes }; writeLS(MILESTONES_KEY, all); return all[idx]
}
export function upsertClaim(claim: ProgressClaim) {
  const all = getLiveClaims(); const idx = all.findIndex(c => c.id === claim.id)
  if (idx >= 0) { all[idx] = claim } else { all.unshift(claim) }
  writeLS(CLAIMS_KEY, all)
}
